import * as React from "react";
import { animate, stagger, utils } from "animejs";
import { CaretRightIcon, LockSimpleIcon, type Icon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@libs/zapi-ui/ds/components/collapsible";
import { useDirection } from "@libs/zapi-ui/ds/components/direction";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar
} from "@libs/zapi-ui/ds/components/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/ds/components/tooltip";
import { useMotionScope } from "@/zapi-frontend/shared/hooks/useMotionScope";
import { distance, duration, ease, staggerStep } from "@libs/zapi-ui/lib/motion";

type ProjectNavItem = {
  id: string;
  label: string;
  href: string;
  icon?: Icon;
  items?: ProjectNavItem[];
  locked?: boolean;
};

type ProjectNavGroup = {
  id: string;
  label?: string;
  items: ProjectNavItem[];
  emptyLabel?: string;
  action?: { label: string; icon: Icon; onClick: () => void };
};

type ProjectNavLink = (item: ProjectNavItem) => React.ReactElement;

const selectedItemClass =
  "rounded-[3px] border-s-2 border-transparent transition-colors hover:border-foreground/15 hover:bg-transparent hover:bg-linear-to-r hover:from-foreground/[0.06] hover:to-transparent hover:text-foreground rtl:hover:bg-linear-to-l data-active:border-primary data-active:bg-transparent data-active:bg-linear-to-r data-active:from-primary/15 data-active:to-transparent data-active:text-primary-950 dark:data-active:text-primary-300 data-active:hover:border-primary data-active:hover:from-primary/20 data-active:hover:text-primary-950 dark:data-active:hover:text-primary-300 rtl:data-active:bg-linear-to-l";

const menuButtonClass = cn(
  "typo-label-md h-9 gap-2.5 px-2.5 text-muted-foreground [&_svg]:size-[18px]",
  selectedItemClass
);

function containsActive(item: ProjectNavItem, activeId: string): boolean {
  return item.id === activeId || Boolean(item.items?.some((child) => containsActive(child, activeId)));
}

function ProjectNavEntry({
  item,
  activeId,
  renderLink,
  lockedLabel,
  lockedIconLabel,
  onNavigate
}: {
  item: ProjectNavItem;
  activeId: string;
  renderLink: ProjectNavLink;
  lockedLabel: string;
  lockedIconLabel: string;
  onNavigate: () => void;
}) {
  const EntryIcon = item.icon;
  const active = item.id === activeId;
  const childActive = containsActive(item, activeId);
  const [open, setOpen] = React.useState(childActive);
  const [openedFor, setOpenedFor] = React.useState(activeId);
  if (openedFor !== activeId) {
    setOpenedFor(activeId);
    if (childActive) setOpen(true);
  }

  if (item.locked) {
    return (
      <SidebarMenuItem data-nav-reveal="">
        <Tooltip>
          <TooltipTrigger
            render={
              <SidebarMenuButton
                aria-disabled="true"
                className={cn(menuButtonClass, "cursor-default opacity-60 hover:border-transparent hover:bg-none")}
              />
            }
          >
            {EntryIcon && <EntryIcon />}
            <span className="flex-1">{item.label}</span>
            <LockSimpleIcon aria-label={lockedIconLabel} className="ms-auto size-3.5!" />
          </TooltipTrigger>
          <TooltipContent side="right">{lockedLabel}</TooltipContent>
        </Tooltip>
      </SidebarMenuItem>
    );
  }

  if (!item.items?.length) {
    return (
      <SidebarMenuItem data-nav-reveal="">
        <SidebarMenuButton
          isActive={active}
          aria-current={active ? "page" : undefined}
          render={renderLink(item)}
          onClick={onNavigate}
          className={menuButtonClass}
        >
          {EntryIcon && <EntryIcon weight={active ? "duotone" : "regular"} />}
          <span>{item.label}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} render={<SidebarMenuItem data-nav-reveal="" />}>
      <CollapsibleTrigger render={<SidebarMenuButton className={cn(menuButtonClass, "group/frame")} />}>
        {EntryIcon && <EntryIcon weight={childActive ? "duotone" : "regular"} />}
        <span className={cn("flex-1", childActive && "text-foreground")}>{item.label}</span>
        <CaretRightIcon
          weight="bold"
          aria-hidden="true"
          className="ms-auto size-3.5! transition-transform duration-200 ease-out group-data-[panel-open]/frame:rotate-90 rtl:-scale-x-100 rtl:group-data-[panel-open]/frame:-rotate-90"
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="h-(--collapsible-panel-height) overflow-hidden transition-[height] duration-200 ease-out data-ending-style:h-0 data-starting-style:h-0">
        <SidebarMenuSub className="mx-[1.125rem] mt-1 gap-0.5 border-foreground/10 px-2">
          {item.items.map((child) => {
            const childIsActive = child.id === activeId;
            if (child.locked) {
              return (
                <SidebarMenuSubItem key={child.id}>
                  <SidebarMenuSubButton
                    aria-disabled="true"
                    className={cn("typo-label-md h-8 px-2.5 text-muted-foreground opacity-60", selectedItemClass)}
                  >
                    <span className="flex-1">{child.label}</span>
                    <LockSimpleIcon aria-label={lockedIconLabel} className="size-3.5" />
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            }
            return (
              <SidebarMenuSubItem key={child.id}>
                <SidebarMenuSubButton
                  isActive={childIsActive}
                  aria-current={childIsActive ? "page" : undefined}
                  render={renderLink(child)}
                  onClick={onNavigate}
                  className={cn("typo-label-md h-8 px-2.5 text-muted-foreground", selectedItemClass)}
                >
                  <span>{child.label}</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            );
          })}
        </SidebarMenuSub>
      </CollapsibleContent>
    </Collapsible>
  );
}

function ProjectNav({
  groups,
  activeId,
  renderLink,
  label,
  lockedLabel,
  lockedIconLabel,
  collapsible = "offcanvas",
  className
}: {
  groups: ProjectNavGroup[];
  activeId: string;
  renderLink: ProjectNavLink;
  label: string;
  lockedLabel: string;
  lockedIconLabel: string;
  collapsible?: "offcanvas" | "none";
  className?: string;
}) {
  const { isMobile, setOpenMobile } = useSidebar();
  const direction = useDirection();
  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false);
  };

  const { root } = useMotionScope<HTMLDivElement>(
    ({ root, reduced }) => {
      if (reduced) return;
      const entries = root.querySelectorAll("[data-nav-reveal]");
      utils.set(entries, { opacity: 0, translateX: direction === "rtl" ? distance.sm : -distance.sm });
      animate(entries, {
        opacity: 1,
        translateX: 0,
        duration: duration.slow,
        delay: stagger(staggerStep.tight, { start: duration.fast }),
        ease: ease.enter
      });
    },
    [isMobile, direction]
  );

  return (
    <Sidebar
      side={direction === "rtl" ? "right" : "left"}
      dir={direction}
      collapsible={collapsible}
      mobileTitle={label}
      className={cn(
        "[--sidebar:transparent]",
        collapsible === "offcanvas" &&
          "top-(--app-header-height) h-[calc(100svh-var(--app-header-height))] bg-linear-to-r from-canvas from-20% via-canvas/75 via-65% to-canvas/0 group-data-[side=left]:border-e-0 group-data-[side=right]:border-s-0 group-data-[side=right]:bg-linear-to-l",
        className
      )}
    >
      <SidebarContent ref={root} data-slot="project-nav" aria-label={label} className="gap-1 px-2 pt-2 pb-6">
        {groups.map((group) => (
          <SidebarGroup key={group.id} className="py-1.5">
            {group.label && (
              <SidebarGroupLabel data-nav-reveal="" className="typo-overline h-7 px-2.5 text-muted-foreground">
                {group.label}
              </SidebarGroupLabel>
            )}
            {group.action && (
              <SidebarGroupAction
                data-nav-reveal=""
                title={group.action.label}
                aria-label={group.action.label}
                onClick={group.action.onClick}
                className="top-2 end-3.5 size-6 rounded-md text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
              >
                <group.action.icon weight="bold" />
              </SidebarGroupAction>
            )}
            {group.items.length > 0 ? (
              <SidebarMenu className="gap-0.5">
                {group.items.map((item) => (
                  <ProjectNavEntry
                    key={item.id}
                    item={item}
                    activeId={activeId}
                    renderLink={renderLink}
                    lockedLabel={lockedLabel}
                    lockedIconLabel={lockedIconLabel}
                    onNavigate={closeOnMobile}
                  />
                ))}
              </SidebarMenu>
            ) : (
              group.emptyLabel && (
                <p data-nav-reveal="" className="typo-caption px-2.5 py-1 text-muted-foreground">
                  {group.emptyLabel}
                </p>
              )
            )}
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
}

export { ProjectNav, type ProjectNavGroup, type ProjectNavItem, type ProjectNavLink };
